import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, Calendar, Clock, FlaskConical } from 'lucide-react';
import { ROUTE_LABS_ORDER_STEP_2 } from '../../../../constants/routes';
import { LabsOrderStepLayout, LabsOrderMarkersModal } from '../components';
import { useLabOrder } from '../labs-order.page';
import { useSubmitAnswer } from '@/app/services/questionnaireV2.service';
import { formatAnswerForSubmission } from '@/app/services/questionnaireV2.service';
import useAnalyticsCalls from '@/hooks/use-analytics';

export function Step1Summary() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeOrder, isLoadingOrder, source, questionnaireId, responseId } = useLabOrder();
  const [isMarkersModalOpen, setIsMarkersModalOpen] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const { callPageView } = useAnalyticsCalls();

  const submitAnswerMutation = useSubmitAnswer(questionnaireId);

  useEffect(() => {
    if (!isLoadingOrder && activeOrder) {
      callPageView({
        status: 'order_already_in_progress',
      });
    }
  }, [isLoadingOrder, activeOrder, callPageView]);

  const handleGetStarted = () => {
    const source = searchParams.get('source');
    const questionnaireId = searchParams.get('questionnaire_id');
    const responseId = searchParams.get('response_id');

    const params = new URLSearchParams();
    if (source) params.set('source', source);
    if (questionnaireId) params.set('questionnaire_id', questionnaireId);
    if (responseId) params.set('response_id', responseId);

    const url = params.toString() ? `${ROUTE_LABS_ORDER_STEP_2}?${params.toString()}` : ROUTE_LABS_ORDER_STEP_2;
    navigate(url);
  };

  const handleResume = () => {
    // Parent component will automatically route to the correct step via centralized routing
    const source = searchParams.get('source');
    const questionnaireId = searchParams.get('questionnaire_id');
    const responseId = searchParams.get('response_id');

    const params = new URLSearchParams();
    if (source) params.set('source', source);
    if (questionnaireId) params.set('questionnaire_id', questionnaireId);
    if (responseId) params.set('response_id', responseId);

    const url = params.toString() ? `${ROUTE_LABS_ORDER_STEP_2}?${params.toString()}` : ROUTE_LABS_ORDER_STEP_2;
    navigate(url);
  };

  const handleSkip = async () => {
    if (!questionnaireId || !responseId) {
      console.error('Missing questionnaire context for skip');
      return;
    }

    setIsSkipping(true);
    try {
      // Submit synthetic answer to questionnaire API
      const answerInput = formatAnswerForSubmission({
        value: 'user_skipped_ordering_labs',
        questionType: 'select',
        units: null,
      });

      if (!answerInput) {
        throw new Error('Failed to format skip answer');
      }

      const result = await submitAnswerMutation.mutateAsync({
        questionnaireId,
        responseId,
        questionSlug: 'labs_order_status',
        data: answerInput,
      });

      // Navigate to next question with direct flag to bypass session restoration
      if (result?.nextQuestion?.slug) {
        navigate(`/risk-assessment/${result.nextQuestion.slug}?direct=true`);
      } else {
        console.error('[Skip] No next question returned from API, falling back to base route');
        navigate('/risk-assessment');
      }
    } catch (error) {
      console.error('Failed to skip labs order:', error);
      // TODO: Show error UI
    } finally {
      setIsSkipping(false);
    }
  };

  const handleBack = () => {
    // Navigate back to previous question in risk assessment with direct flag
    if (source === 'risk_assessment') {
      const previousQuestionSlug = searchParams.get('previous_question_slug');
      if (previousQuestionSlug) {
        navigate(`/risk-assessment/${previousQuestionSlug}?direct=true`);
      } else {
        // Fallback to interstitial if no previous question available
        navigate(`/risk-assessment/section_interstitial_catch_labs?direct=true`);
      }
    } else {
      navigate(-1);
    }
  };

  return (
    <LabsOrderStepLayout
      currentStep={1}
      onBack={source === 'risk_assessment' ? handleBack : undefined}
      footer={
        isLoadingOrder ? (
          <div className="flex justify-center">
            <div className="py-4 text-center">
              <p className="text-[#9E9E9E]">Loading...</p>
            </div>
          </div>
        ) : activeOrder ? (
          <div className="flex flex-col items-center gap-4">
            <div className="w-full rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-center text-sm text-blue-800">
                You have an order in progress. Click below to continue.
              </p>
            </div>
            <div className="btn-primary-container">
              <button onClick={handleResume} className="btn-primary">
                Resume Your Order
              </button>
            </div>
          </div>
        ) : (
          <div className="sticky-bottom-controls w-full pt-2 pb-4">
            <div className="controls-container">
              <div className="controls-overlay">
                {source === 'risk_assessment' && (
                  <div className="skip-display">
                    <button
                      onClick={handleSkip}
                      disabled={isSkipping}
                      className="skip-display-button"
                      style={{
                        cursor: isSkipping ? 'not-allowed' : 'pointer',
                        opacity: isSkipping ? 0.5 : 1,
                      }}
                    >
                      {isSkipping ? 'Skipping...' : 'Skip for now'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="btn-primary-container">
              <button onClick={handleGetStarted} className="btn-primary">
                Get Started
              </button>
            </div>
          </div>
        )
      }
    >
      <div className="space-y-6">
        {/* Title */}
        <h1
          className="text-center font-semibold tracking-[-0.5px]"
          style={{
            marginBottom: 'var(--spacing-6)',
            fontSize: 'var(--font-size-desktop-h3)',
            lineHeight: 'var(--line-height-normal)',
            color: 'var(--color-catch-dark-blue-90)',
            fontFamily: 'var(--font-family-inter)',
          }}
        >
          Order Your Bloodwork
        </h1>

        {/* What's Included Card */}
        <div
          className="mx-auto max-w-md rounded-2xl p-6"
          style={{
            background:
              'linear-gradient(180deg, rgba(201, 238, 254, 0.00) 0%, rgba(202, 239, 255, 0.50) 99.99%, #CBEFFF 100%), #F0F5F6',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {/* Icon and Title */}
          <div className="mb-3 flex items-start gap-3">
            <FlaskConical className="mt-1 text-[#5BC4BF]" size={16} />
            <h3
              className="font-semibold"
              style={{
                fontSize: 'var(--font-size-body)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-inter)',
              }}
            >
              What's Included - No added cost
            </h3>
          </div>

          {/* Description */}
          <p
            className="mb-3 text-left"
            style={{
              fontSize: 'var(--font-size-body)',
              lineHeight: 'var(--line-height-loose)',
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-family-inter)',
            }}
          >
            Deeper insights about your cancer risk
          </p>

          {/* Markers Info */}
          <p
            className="mb-2 text-left"
            style={{
              fontSize: 'var(--font-size-body)',
              lineHeight: 'var(--line-height-loose)',
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-family-inter)',
            }}
          >
            <strong>50+ Markers</strong> including tests for liver &amp; kidney function, metabolic function, and
            inflammation.
          </p>

          <p
            className="mb-2 text-left"
            style={{
              fontSize: 'var(--font-size-body)',
              lineHeight: 'var(--line-height-loose)',
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-family-inter)',
            }}
          >
            <strong>Biological age</strong> and how it impacts your risk
          </p>
          {/* See all link */}
          <button
            onClick={() => setIsMarkersModalOpen(true)}
            className="font-medium text-[#5BC4BF] hover:underline"
            style={{
              fontSize: 'var(--font-size-body)',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            See all
          </button>
        </div>

        {/* Feature Sections */}
        <div className="mx-auto flex max-w-md flex-col py-2">
          <div className="space-y-4">
            {/* Select a lab near you */}
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#E0F5F4]">
                <MapPin className="text-[#5BC4BF]" size={20} />
              </div>
              <div>
                <h3
                  className="mb-1 font-semibold"
                  style={{
                    fontSize: 'var(--font-size-body)',
                    lineHeight: 'var(--line-height-relaxed)',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'var(--font-family-inter)',
                  }}
                >
                  Select a lab near you
                </h3>
                <p
                  className="text-left"
                  style={{
                    fontSize: 'var(--font-size-body)',
                    lineHeight: 'var(--line-height-loose)',
                    color: 'var(--color-text-secondary)',
                    fontFamily: 'var(--font-family-inter)',
                  }}
                >
                  Choose from thousands of locations
                </p>
              </div>
            </div>

            {/* Walk in or schedule */}
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#E0F5F4]">
                <Calendar className="text-[#5BC4BF]" size={20} />
              </div>
              <div>
                <h3
                  className="mb-1 font-semibold"
                  style={{
                    fontSize: 'var(--font-size-body)',
                    lineHeight: 'var(--line-height-relaxed)',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'var(--font-family-inter)',
                  }}
                >
                  Walk in or schedule
                </h3>
                <p
                  className="text-left"
                  style={{
                    fontSize: 'var(--font-size-body)',
                    lineHeight: 'var(--line-height-loose)',
                    color: 'var(--color-text-secondary)',
                    fontFamily: 'var(--font-family-inter)',
                  }}
                >
                  No fasting required
                </p>
              </div>
            </div>

            {/* Quick blood draw */}
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#E0F5F4]">
                <Clock className="text-[#5BC4BF]" size={20} />
              </div>
              <div>
                <h3
                  className="mb-1 font-semibold"
                  style={{
                    fontSize: 'var(--font-size-body)',
                    lineHeight: 'var(--line-height-relaxed)',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'var(--font-family-inter)',
                  }}
                >
                  Quick blood draw
                </h3>
                <p
                  className="text-left"
                  style={{
                    fontSize: 'var(--font-size-body)',
                    lineHeight: 'var(--line-height-loose)',
                    color: 'var(--color-text-secondary)',
                    fontFamily: 'var(--font-family-inter)',
                  }}
                >
                  Takes about 10-15 minutes
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Markers Modal */}
      <LabsOrderMarkersModal isOpen={isMarkersModalOpen} onClose={() => setIsMarkersModalOpen(false)} />
    </LabsOrderStepLayout>
  );
}
